import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { aumService } from './aum.service';
import { handleThunkError } from '../helpers/thunkError';

const initialState = {
  advisorsAcrossProviders: [],
  providerAum: [],
  advisorBookSummary: [],
  providerBookSummary: [],
  aumStats: [],
  fullBookTotal: [],
  fullBook: [],

  loadingFullBook: false,
  loadingFullBookTotal: false,
  loadingAumStats: false,
  loadingProviderBookSummary: false,
  loadingAdvisorsAcrossProviders: false,
  loadingProviderAum: false,
  loadingAdvisorBookSummary: false,

  expandedProvider: false,
  expandedAdvisor: false,

  // ✅ UI STATE
  bookAnalysisUI: {
    isMonthEnd: true,
    selectedMonth: null
  },

  error: null
};

// ----------------------------
// AUM FULL BOOK
// ----------------------------

export const getFullBook = createAsyncThunk('aum/getFullBook', async (payload, thunkAPI) => {
  try {
    const response = await aumService.getFullBook(payload);
    return response;
  } catch (error) {
    return handleThunkError(error, thunkAPI, 'Failed to fetch full book');
  }
});

// ----------------------------
// AUM FULL BOOK TOTAL
// ----------------------------
export const getFullBookTotal = createAsyncThunk(
  'aum/getFullBookTotal',
  async (payload, thunkAPI) => {
    try {
      const response = await aumService.getFullBookTotal(payload);
      return response;
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch full book total');
    }
  }
);

// ----------------------------
// AUM GROWTH STATS
// ----------------------------
export const getAumStats = createAsyncThunk('aum/getAumStats', async (payload, thunkAPI) => {
  try {
    const response = await aumService.getAumStats(payload);
    return response;
  } catch (error) {
    return handleThunkError(error, thunkAPI, 'Failed to fetch AUM stats');
  }
});

// ----------------------------
// ACROSS PROVIDERS
// ----------------------------
export const getAdvisorsAcrossProviders = createAsyncThunk(
  'aum/getAdvisorsAcrossProviders',
  async (payload, thunkAPI) => {
    try {
      const response = await aumService.getAdvisorsAcrossProviders(payload);
      return response;
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch advisors accross providers');
    }
  }
);

// ----------------------------
// PROVIDER AUM
// ----------------------------
export const getProviderAum = createAsyncThunk('aum/getProviderAum', async (payload, thunkAPI) => {
  try {
    const response = await aumService.getProviderAum(payload);
    return response;
  } catch (error) {
    return handleThunkError(error, thunkAPI, 'Failed to fetch provider AUM');
  }
});

// ----------------------------
// ADVISOR BOOK SUMMARY
// ----------------------------
export const getAdvisorBookSummary = createAsyncThunk(
  'aum/getAdvisorBookSummary',
  async (payload, thunkAPI) => {
    try {
      const response = await aumService.getAdvisorBookSummary(payload);
      return response;
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch advisor book summary');
    }
  }
);

// ----------------------------
// PROVIDER BOOK SUMMARY
// ----------------------------
export const getProviderBookSummary = createAsyncThunk(
  'aum/getProviderBookSummary',
  async (payload, thunkAPI) => {
    try {
      const response = await aumService.getProviderBookSummary(payload);
      return response;
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch provider book summary');
    }
  }
);

export const aumSlice = createSlice({
  name: 'aum',
  initialState,

  reducers: {
    setExpandedProvider: (state) => {
      state.expandedProvider = !state.expandedProvider;
    },

    setExpandedAdvisor: (state) => {
      state.expandedAdvisor = !state.expandedAdvisor;
    },

    // ✅ SAVE UI STATE
    setBookAnalysisUI: (state, action) => {
      state.bookAnalysisUI = {
        ...state.bookAnalysisUI,
        ...action.payload
      };
    },

    // ✅ CLEAR UI STATE
    clearBookAnalysisUI: (state) => {
      state.bookAnalysisUI = {
        isMonthEnd: false,
        selectedMonth: null
      };
    }
  },

  extraReducers: (builder) => {
    builder
      // ----------------------------
      // AUM FULL BOOK
      // ----------------------------
      .addCase(getFullBook.pending, (state) => {
        state.loadingFullBook = true;
        state.error = null;
      })
      .addCase(getFullBook.fulfilled, (state, action) => {
        state.loadingFullBook = false;
        state.fullBook = action.payload;
      })
      .addCase(getFullBook.rejected, (state, action) => {
        state.loadingFullBook = false;
        state.error = action.payload;
      })
      // ----------------------------
      // AUM STATS
      // ----------------------------
      .addCase(getAumStats.pending, (state) => {
        state.loadingAumStats = true;
        state.error = null;
      })
      .addCase(getAumStats.fulfilled, (state, action) => {
        state.loadingAumStats = false;
        state.aumStats = action.payload;
      })
      .addCase(getAumStats.rejected, (state, action) => {
        state.loadingAumStats = false;
        state.error = action.payload;
      })
      // ----------------------------
      // AUM FULL BOOK TOTAL
      // ----------------------------
      .addCase(getFullBookTotal.pending, (state) => {
        state.loadingFullBookTotal = true;
        state.error = null;
      })
      .addCase(getFullBookTotal.fulfilled, (state, action) => {
        state.loadingFullBookTotal = false;
        state.fullBookTotal = action.payload;
      })
      .addCase(getFullBookTotal.rejected, (state, action) => {
        state.loadingFullBookTotal = false;
        state.error = action.payload;
      })
      // ----------------------------
      // ACROSS PROVIDERS
      // ----------------------------
      .addCase(getAdvisorsAcrossProviders.pending, (state) => {
        state.loadingAdvisorsAcrossProviders = true;
        state.error = null;
      })
      .addCase(getAdvisorsAcrossProviders.fulfilled, (state, action) => {
        state.loadingAdvisorsAcrossProviders = false;
        state.advisorsAcrossProviders = action.payload;
      })
      .addCase(getAdvisorsAcrossProviders.rejected, (state, action) => {
        state.loadingAdvisorsAcrossProviders = false;
        state.error = action.payload;
      })

      // ----------------------------
      // PROVIDER AUM
      // ----------------------------
      .addCase(getProviderAum.pending, (state) => {
        state.loadingProviderAum = true;
        state.error = null;
      })
      .addCase(getProviderAum.fulfilled, (state, action) => {
        state.loadingProviderAum = false;
        state.providerAum = action.payload;
      })
      .addCase(getProviderAum.rejected, (state, action) => {
        state.loadingProviderAum = false;
        state.error = action.payload;
      })

      // ----------------------------
      // ADVISOR BOOK SUMMARY
      // ----------------------------
      .addCase(getAdvisorBookSummary.pending, (state) => {
        state.loadingAdvisorBookSummary = true;
        state.error = null;
      })
      .addCase(getAdvisorBookSummary.fulfilled, (state, action) => {
        state.loadingAdvisorBookSummary = false;
        state.advisorBookSummary = action.payload;
      })
      .addCase(getAdvisorBookSummary.rejected, (state, action) => {
        state.loadingAdvisorBookSummary = false;
        state.error = action.payload;
      })

      // ----------------------------
      // PROVIDER BOOK SUMMARY
      // ----------------------------
      .addCase(getProviderBookSummary.pending, (state) => {
        state.loadingProviderBookSummary = true;
      })
      .addCase(getProviderBookSummary.fulfilled, (state, action) => {
        state.loadingProviderBookSummary = false;
        state.providerBookSummary = action.payload;
      })
      .addCase(getProviderBookSummary.rejected, (state) => {
        state.loadingProviderBookSummary = false;
      });
  }
});

export const { setExpandedProvider, setExpandedAdvisor, setBookAnalysisUI, clearBookAnalysisUI } =
  aumSlice.actions;

export default aumSlice.reducer;
